import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Modal, Space, Tooltip } from "antd";
import { useMemo, useState } from "react";
import { getDaysText } from "utils/utils";
import { FeatureTypes } from "entity-app/constants";
import DisabledPlusAddIcon from "components/svg-icons/disabled-plus-icon";
import { ComputeStrategy, TEditMilestone } from "./model";
import EditableInput from "./components/editable-input";
import AddMilestonePanel from "./components/add-milestone-panel";
import { VRules } from "./validation-rules";
import EditablePopover from "./components/editable-popover";
import DeleteConfirmModel from "./components/delete-confirm-modal";
import AddOffsetPanel from "./components/add-offset-panel";

const floatLabel = {
  [FeatureTypes.MATERIALS]: "Material Planned Float",
  [FeatureTypes.BID_PACKAGE]: "Bid Package Planned Float"
};

const lastOffsetApplicableForFeatureTypes = [FeatureTypes.MATERIALS];

function isDurationEditable(
  templateComputeStrategy: ComputeStrategy,
  isLast: boolean
) {
  return (
    templateComputeStrategy === ComputeStrategy.DURATION_DRIVEN ||
    (templateComputeStrategy === ComputeStrategy.COMMITTED_DATE_DRIVEN &&
      isLast)
  );
}

function MilestoneOffsetDuration({
  milestone,
  disable,
  isTemplateUsed,
  templateComputeStrategy,
  isLast,
  onDurationUpdate
}: {
  milestone: TEditMilestone;
  disable: boolean;
  isTemplateUsed: boolean;
  templateComputeStrategy: ComputeStrategy;
  isLast: boolean;
  onDurationUpdate: (duration: number) => void;
}) {
  if (!isDurationEditable(templateComputeStrategy, isLast)) {
    return (
      <div className="text-sm text-ciq-black-a45 mr-7">(System calculated)</div>
    );
  }

  return (
    <EditableInput
      disable={disable}
      isTemplateUsed={isTemplateUsed}
      className="text-milestone"
      classNameEdit="text-edit-offset"
      inputText={`(${milestone.duration} ${getDaysText(milestone.duration)})`}
      input={milestone.duration}
      onSubmit={onDurationUpdate}
      rules={VRules.Duration}
    />
  );
}

function FloatLabelWithEditableInput({
  disable,
  isTemplateUsed,
  floatState,
  onFloatUpdate
}: {
  disable: boolean;
  isTemplateUsed: boolean;
  floatState: { float: number };
  onFloatUpdate: (duration: string) => void;
}) {
  if (disable) {
    return (
      <span className="text-sm font-semibold pl-8">
        {`${floatState.float} ${getDaysText(floatState.float)}`}
      </span>
    );
  }

  return (
    <EditableInput
      disable={disable}
      isTemplateUsed={isTemplateUsed}
      className="text-sm font-semibold"
      classNameEdit="text-edit-offset"
      inputText={`${floatState.float} ${getDaysText(floatState.float)}`}
      input={floatState.float}
      onSubmit={onFloatUpdate}
      rules={VRules.Duration}
    />
  );
}

function LastMilestoneHorizontalLine({
  milestone,
  showFloat,
  derivedFloatLabel,
  disable,
  isTemplateUsed,
  featureId,
  floatState,
  onFloatUpdate,
  onRemoveOffset,
  onOpenOffsetModel
}: {
  milestone: TEditMilestone;
  showFloat: boolean;
  derivedFloatLabel: string;
  disable: boolean;
  isTemplateUsed: boolean;
  featureId: FeatureTypes;
  floatState: { float: number };
  onFloatUpdate: (duration: string) => void;
  onRemoveOffset: () => void;
  onOpenOffsetModel: () => void;
}) {
  const floatInput = (
    <FloatLabelWithEditableInput
      disable={disable}
      isTemplateUsed={isTemplateUsed}
      floatState={floatState}
      onFloatUpdate={onFloatUpdate}
    />
  );

  if (milestone.offsetName) {
    return (
      <div className="flex items-center">
        <div className="bl-line-last" />
        <div className="relative shrink-0">
          <div className="pb-[6px] absolute bottom-[calc(100%+5px)] left-0 right-0 text-center break-words text-sm font-medium">
            {derivedFloatLabel}
          </div>
          <div className="bl-rect bg-gray-300" />
          <div className="absolute top-[calc(100%+24px)] -left-10 flex items-center gap-3 whitespace-nowrap bg-ciq-neutral-50 px-3 py-2">
            <span className="text-sm">Planned Float</span>
            {floatInput}
          </div>
        </div>
        <div className="relative w-48">
          <div className="bl-line !w-48" />
        </div>
        <div className="bl-rect flex justify-end items-center">
          {!disable && isTemplateUsed && (
            <div className="items-center">
              <DeleteOutlined className=" opacity-25 pr-1" />
            </div>
          )}
          {!disable && !isTemplateUsed && (
            <Tooltip title="Remove Duration">
              <DeleteOutlined
                className="cursor-pointer px-1 hover:bg-black/5"
                onClick={onRemoveOffset}
              />
            </Tooltip>
          )}
        </div>
        <div className="bl-left-emplty" />
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <div className="bl-line-last" />
      {showFloat && (
        <div className="relative shrink-0">
          <div className="pb-[6px] absolute bottom-[calc(100%+5px)] left-0 right-0 text-center break-words text-sm font-medium">
            {derivedFloatLabel}
          </div>
          <div className="bl-rect bg-gray-300" />
          <div className="absolute top-[calc(100%+24px)] -left-10 flex items-center gap-3 whitespace-nowrap bg-ciq-neutral-50 px-3 py-2">
            <span className="text-sm">Planned Float</span>
            {floatInput}
          </div>
        </div>
      )}
      <div className="flex items-center ">
        {!disable && lastOffsetApplicableForFeatureTypes.includes(featureId) ? (
          <>
            <div className="bl-line" />
            <Tooltip title="Add Duration">
              <Space onClick={onOpenOffsetModel}>
                <PlusOutlined className="cursor-pointer bg-ciq-gray-300 px-1 py-1 hover:bg-ciq-gray-400" />
              </Space>
            </Tooltip>
          </>
        ) : null}
      </div>
      <div className="bl-left-emplty" />
    </div>
  );
}

function MilestoneEditor({
  milestone,
  index,
  allMilestones,
  setMilestones,
  disable = false,
  floatState,
  featureId,
  showFloat,
  isTemplateUsed,
  templateComputeStrategy
}: {
  milestone: TEditMilestone;
  index: number;
  allMilestones: TEditMilestone[];
  setMilestones: React.Dispatch<React.SetStateAction<TEditMilestone[]>>;
  disable?: boolean;
  isTemplateUsed: boolean;
  floatState: { setFloat: (value: number) => void; float: number };
  featureId: FeatureTypes;
  showFloat: boolean;
  templateComputeStrategy: ComputeStrategy;
}) {
  const isFirst = index === 0;
  const isLast = index === allMilestones.length - 1;

  const setCurrentMilestone = (newMilestone: TEditMilestone) => {
    const tempAllMileStones = [...allMilestones];
    tempAllMileStones.splice(index, 1, newMilestone);
    setMilestones(tempAllMileStones);
  };

  // Model to add new Milestone
  const [openAddMilestonePanel, setOpenAddMilestonePanel] = useState(false);
  const onOpenMilestoneModel = () => {
    setOpenAddMilestonePanel(true);
  };
  const onAddMilestone = (newMilestone: TEditMilestone) => {
    const tempAllMileStones = [...allMilestones];
    tempAllMileStones.splice(index, 0, newMilestone);
    setMilestones(tempAllMileStones);
  };
  const addMilestoneModel = openAddMilestonePanel && (
    <AddMilestonePanel
      isOpen={openAddMilestonePanel}
      setOpen={setOpenAddMilestonePanel}
      milestones={allMilestones}
      onAdd={onAddMilestone}
      templateComputeStrategy={templateComputeStrategy}
    />
  );

  // Add Last Offset Data
  const [openAddOffset, setOpenAddOffset] = useState(false);
  const onOpenOffsetModel = () => {
    setOpenAddOffset(true);
  };
  const onAddOffset = (data: { duration: number; offsetName: string }) => {
    const tempMilestone = {
      ...milestone,
      ...data
    };
    setCurrentMilestone(tempMilestone);
  };
  const addOffsetModel = openAddOffset && (
    <AddOffsetPanel
      isOpen={openAddOffset}
      setOpen={setOpenAddOffset}
      milestones={allMilestones}
      onAdd={onAddOffset}
    />
  );

  // Remove selected milestone
  const [removingMilestone, setRemovingMilestone] = useState<TEditMilestone>();
  const onOpenDeleteMilestonModel = () => setRemovingMilestone(milestone);
  const onRemoveMilestone = (milestoneId: string) => {
    const tempAllMileStones = allMilestones.filter((m) => m.id !== milestoneId);
    setMilestones(tempAllMileStones);
  };
  const removeMilestoneModel = removingMilestone && (
    <DeleteConfirmModel
      milestone={removingMilestone}
      setOpen={setRemovingMilestone}
      onConfirm={onRemoveMilestone}
    />
  );

  // Remove Last Offset
  const onRemoveOffset = () => {
    Modal.confirm({
      title: "Are you sure you want to delete this duration?",
      onOk() {
        const tempMilestone = {
          ...milestone,
          offsetName: null,
          duration: null
        };
        setCurrentMilestone(tempMilestone);
      },
      // antd v6 changed the button class list (exact `ant-btn ant-btn-primary
      // w-24` no longer matches), so give the confirm modal's OK button a
      // class-independent data-testid for UI automation.
      okButtonProps: {
        className: "w-24",
        "data-testid": "milestone-editor-confirm-ok-button"
      },
      cancelButtonProps: {
        className: "w-24"
      },
      okText: "Yes"
    });
  };

  // Rename offset Name of milestone
  const onRenameOffsetName = (offsetName: string) => {
    const tempMilestone = { ...milestone, offsetName };
    setCurrentMilestone(tempMilestone);
  };

  // Update offset duration of milestone
  const onDurationUpdate = (duration: number) => {
    const tempMilestone = { ...milestone, duration: Number(duration) };
    setCurrentMilestone(tempMilestone);
  };

  // Rename the name of milestone
  const onRenameMilestoneName = (name: string) => {
    const tempMilestone = { ...milestone, name };
    setCurrentMilestone(tempMilestone);
  };

  // Rename Milestone Action
  const onRenameMilestoneAction = (milestone_action: string) => {
    const tempMilestone = {
      ...milestone,
      milestone_action
    };
    setCurrentMilestone(tempMilestone);
  };

  const onFloatUpdate = (duration: string) => {
    floatState.setFloat(Number(duration));
  };

  const actionInfoModelDiv = (
    <EditablePopover
      placeholder="Write about the milestone here.."
      disable={disable}
      message={(milestone.milestone_action || "").trim()}
      onSubmit={onRenameMilestoneAction}
      rules={
        templateComputeStrategy === ComputeStrategy.DURATION_DRIVEN
          ? VRules.ActionInfo
          : []
      }
      placeholderForEmptyMessage="No Milestone Action added"
    />
  );

  const offsetDiv = milestone.offsetName && (
    <div className="w-80 h-16 shrink-0 flex-col space-y-2 justify-end">
      <div className="w-full  flex justify-end">
        <EditableInput
          disable={disable}
          isTemplateUsed={isTemplateUsed}
          className="text-milestone"
          classNameEdit="text-edit-offset"
          input={milestone.offsetName}
          onSubmit={onRenameOffsetName}
          rules={VRules.OffsetName(
            allMilestones.filter((x) => x.id !== milestone.id)
          )}
        />
      </div>
      <div className="w-full flex justify-end">
        <MilestoneOffsetDuration
          milestone={milestone}
          disable={disable}
          isTemplateUsed={isTemplateUsed}
          templateComputeStrategy={templateComputeStrategy}
          isLast={isLast}
          onDurationUpdate={onDurationUpdate}
        />
      </div>
    </div>
  );

  const derivedFloatLabel = useMemo(() => {
    return floatLabel[featureId as keyof typeof floatLabel] || "Float";
  }, [featureId]);

  const lastOffsetDiv =
    isLast && showFloat ? (
      <div className="flex">
        <div className="w-96 h-16 shrink-0 ml-[25px]" />
        {offsetDiv}
      </div>
    ) : (
      <div className="h-16" />
    );

  const horizontalLineInitialPaddingDiv = isFirst ? (
    <div className="bl-left-emplty" />
  ) : (
    <div className="bl-line-left " />
  );
  const horizontalLineRectangleDiv = (
    <div className="flex items-center">
      <div className="bl-line" /> <div className="bl-rect" />
    </div>
  );
  const horizontalLineDiv = (
    <div className="flex w-full items-center">
      {horizontalLineInitialPaddingDiv}
      <div className="bl-circle" />
      {isLast ? (
        <LastMilestoneHorizontalLine
          milestone={milestone}
          showFloat={showFloat}
          derivedFloatLabel={derivedFloatLabel}
          disable={disable}
          isTemplateUsed={isTemplateUsed}
          featureId={featureId}
          floatState={floatState}
          onFloatUpdate={onFloatUpdate}
          onRemoveOffset={onRemoveOffset}
          onOpenOffsetModel={onOpenOffsetModel}
        />
      ) : (
        horizontalLineRectangleDiv
      )}
    </div>
  );

  const addMilestoneDiv = (
    <div className="flex w-6 items-center pl-1">
      {isTemplateUsed && !disable && allMilestones.length < 10 && (
        <Space>
          <DisabledPlusAddIcon />
        </Space>
      )}
      {!isTemplateUsed && !disable && allMilestones.length < 10 && (
        <Tooltip title="Add Milestone">
          <Space onClick={onOpenMilestoneModel}>
            <PlusOutlined className="cursor-pointer bg-ciq-gray-300 px-1 py-1 hover:bg-ciq-gray-400" />
          </Space>
        </Tooltip>
      )}
    </div>
  );

  const milestoneHeaderDiv = (
    <div className="flex p-2 h-12 border-solid border-0 border-b border-inherit justify-between">
      <EditableInput
        disable={disable}
        isTemplateUsed={isTemplateUsed}
        className="text-milestone"
        classNameEdit="text-xs max-w-[130px]"
        input={milestone.name}
        onSubmit={onRenameMilestoneName}
        rules={VRules.MilestoneName(
          allMilestones.filter((x) => x.id !== milestone.id)
        )}
      />
      <div className="flex space-x-2 items-center">{actionInfoModelDiv}</div>
    </div>
  );

  const milestoneBottomDiv = (
    <div
      className="p-2 h-16 flex justify-end items-end"
      style={{ width: "230px" }}
    >
      {!disable && isTemplateUsed && !isLast && allMilestones.length > 2 && (
        <DeleteOutlined className="opacity-25" />
      )}
      {!disable && !isTemplateUsed && !isLast && allMilestones.length > 2 && (
        <Tooltip title="Remove Milestone">
          <DeleteOutlined
            className="cursor-pointer hover:bg-black/5"
            onClick={onOpenDeleteMilestonModel}
          />
        </Tooltip>
      )}
    </div>
  );

  return (
    <div className="pb-5 shrink-0 flex-col relative">
      <div className="h-[6rem]">
        <div className="w-80 absolute left-10 top-4 flex-col">
          {isLast ? lastOffsetDiv : offsetDiv}
          {horizontalLineDiv}
        </div>
      </div>

      <div className="w-full flex space-x-3 items-center px-1 mt-12">
        {addMilestoneDiv}
        <div className="w-full flex-col border border-solid ">
          {milestoneHeaderDiv}
          {milestoneBottomDiv}
        </div>
      </div>
      {addMilestoneModel}
      {removeMilestoneModel}
      {addOffsetModel}
    </div>
  );
}

export default MilestoneEditor;
